import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  TrendingUp,
  Sparkles,
  Target,
  MessageCircle,
  ShieldAlert,
  AlertTriangle,
  Code2,
  Award,
  Compass,
  Layers,
  Zap,
  CheckCircle2,
  Building2,
  Briefcase,
  RefreshCw,
  PlayCircle,
  Rocket,
  Brain,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import HeroTemplateEnts from '../../components/sections/HeroTemplateEnts';
import IADossierModal from '../../components/ia-dossier/IADossierModal';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';
const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';
const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';
const h3Class = 'text-xl font-semibold text-[#30475e] mb-3 leading-snug';
const bodyClass = 'text-base text-neutral-600 leading-relaxed';
const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

const adoptionBlockers = [
  {
    icon: AlertTriangle,
    title: 'Trop technique pour vos équipes',
    description:
      "Les solutions IA disponibles supposent souvent un product owner technique, des data engineers, des ML ops. La PME n'a ni l'équipe ni le budget. Résultat : on n'y va pas.",
  },
  {
    icon: AlertTriangle,
    title: 'Trop coûteux à internaliser',
    description:
      "Recruter un consultant IA senior coûte 100-180 k€/an. Faire appel à un Big 4 sur un projet IA RH, c'est 80-200 k€ pour le cadrage avant même le premier livrable.",
  },
  {
    icon: AlertTriangle,
    title: 'Pas dédié au métier RH',
    description:
      "Les solutions IA généralistes (data science, automation) ne comprennent ni la mécanique RH, ni les enjeux humains, ni le cadre juridique social. Le résultat tombe à côté.",
  },
];

const conviction = [
  {
    icon: Award,
    title: 'Expertise RH senior',
    description:
      "Vingt ans de direction RH en scale-up et contexte dégradé. On sait ce qu'un DRH doit pouvoir mesurer, anticiper, automatiser. On sait aussi ce qui ne doit JAMAIS être délégué à une IA.",
  },
  {
    icon: Code2,
    title: 'Ingénierie IA appliquée',
    description:
      "Maîtrise des LLM (OpenAI, Anthropic, Mistral), des frameworks IA (LangChain, vector DBs), des stacks no-code RAG, des edge functions Supabase, du fine-tuning ciblé sur le langage RH.",
  },
];

const useCases = [
  {
    icon: Bot,
    badge: 'Chatbots RH',
    title: 'Assistants conversationnels 24/7',
    description:
      "Trois personas typiques : Assistant candidat (réponses sur le poste, l'entreprise, le process), Assistant onboarding (paperasse, premiers pas, FAQ collaborateur), Assistant RH interne (congés, paie, droits). Déployés sur votre site, votre intranet ou Slack/Teams.",
    examples: [
      'Bot candidat : -70 % d’emails RH répétitifs sur 1 mois',
      'Bot onboarding : 90 jours d’intégration scriptés',
      'Bot collaborateur : niveau 1 RH automatisé',
    ],
  },
  {
    icon: TrendingUp,
    badge: 'IA prédictive',
    title: 'Workforce planning et anticipation',
    description:
      "Modèles de prévision pour anticiper vos besoins en recrutement (volumes par fonction, horizon 3/6/12 mois), repérer les profils à risque de turnover (signaux faibles dans les données RH), et prévoir l'évolution des compétences requises par votre marché.",
    examples: [
      'Prévision besoins recrutement à 12 mois',
      'Score de risque turnover par collaborateur',
      'Cartographie prospective des compétences clés',
    ],
  },
  {
    icon: Sparkles,
    badge: 'IA générative',
    title: 'Contenus RH sur-mesure',
    description:
      "Génération automatique d'offres d'emploi attractives à partir d'un brief, fiches de poste structurées depuis un enregistrement vocal manager, plans d'intégration personnalisés par profil, contenus marque employeur (posts LinkedIn, page carrière, témoignages enrichis).",
    examples: [
      'Offre d’emploi générée et tunée à votre ton de marque',
      'Fiche de poste depuis brief vocal de 5 min',
      'Plan d’onboarding 30/60/90 personnalisé',
    ],
  },
  {
    icon: Target,
    badge: 'IA de scoring',
    title: 'Compatibilité et matching sémantique',
    description:
      "Scoring CV ↔ poste avec une lecture sémantique fine du langage métier (vs simple matching de mots-clés). Compatibilité candidat ↔ équipe via notre Mapping RH déjà déployé. Détection de candidats passifs sur LinkedIn par signaux comportementaux.",
    examples: [
      'Score sémantique CV ↔ poste, lecture du métier',
      'Mapping RH cultural fit (déjà en production)',
      'Sourcing candidats passifs ciblé par IA',
    ],
  },
  {
    icon: MessageCircle,
    badge: 'IA conversationnelle',
    title: 'Préparation et coaching candidat',
    description:
      "Simulations d'entretien personnalisées par profil (questions adaptées, debrief contextuel), coaching écrit pour préparer les candidats à votre culture, accompagnement IA de la prise de poste pour les premières semaines.",
    examples: [
      'Simulation entretien GPT-4 personnalisée',
      'Coaching écrit ciblé sur votre culture',
      'Compagnon IA des 30 premiers jours',
    ],
  },
  {
    icon: ShieldAlert,
    badge: 'IA décisionnelle',
    title: 'Détection précoce et fidélisation',
    description:
      "Détection des signaux faibles de risques sociaux (RPS, démotivation, conflits d'équipe) à partir de vos données RH anonymisées. Recommandations de fidélisation individualisées. Aide à la décision sur les sujets sensibles (rétention de talents critiques).",
    examples: [
      'Score RPS par équipe (anonymisé)',
      'Recommandation fidélisation individualisée',
      'Aide à la décision rétention top performers',
    ],
  },
];

const methodSteps = [
  {
    icon: Compass,
    title: 'Cadrage gratuit (1 h)',
    description:
      "Identification du besoin réel (vs besoin perçu), des données disponibles, du ROI attendu. Si l'IA n'est pas la bonne solution, nous vous orientons vers une approche plus simple. Pas de gloubi-boulga IA pour faire du IA.",
  },
  {
    icon: Layers,
    title: 'POC chiffré (2 à 4 semaines)',
    description:
      "Un livrable concret, restreint, mesurable. Vous voyez le résultat avant de vous engager sur un déploiement complet. Si le POC ne tient pas ses promesses, on s'arrête là — sans surcoût caché.",
  },
  {
    icon: Zap,
    title: 'Déploiement opérationnel',
    description:
      "Mise en production sur votre infrastructure (ou la nôtre selon préférence), formation de votre équipe, documentation complète. L'objectif : que vous reprenez la main sans dépendance technique perpétuelle.",
  },
  {
    icon: RefreshCw,
    title: 'Mesure et amélioration continue',
    description:
      "Métriques d'usage, KPI métier, retours utilisateurs. Ajustements trimestriels. L'IA ne sert à rien si elle n'est pas mesurée — chaque déploiement vient avec son tableau de bord.",
  },
];

const fitProfiles = [
  {
    icon: Building2,
    title: 'PME et ETI 50-500 collaborateurs',
    description:
      "Vous voulez moderniser la fonction RH sans embaucher un consultant IA à temps plein. Vous avez des cas d'usage clairs mais pas l'équipe pour les construire.",
  },
  {
    icon: Briefcase,
    title: 'DRH submergés sur des projets stratégiques',
    description:
      "Votre DRH a besoin de bras techniques pour automatiser les tâches récurrentes et libérer du temps pour les sujets à forte valeur ajoutée.",
  },
  {
    icon: Rocket,
    title: 'Entreprises en transformation',
    description:
      "Restructuration, fusion, croissance accélérée : vous avez besoin d'outils IA pour anticiper, mesurer, décider — pas pour faire joli sur un slide.",
  },
  {
    icon: Brain,
    title: 'Acteurs B2C avec exigence marque employeur',
    description:
      "Retail, restauration, services, télécom : votre marque employeur impacte directement vos ventes. Les chatbots, IA générative et IA de fidélisation sont rentables au premier semestre.",
  },
];

const differentiators = [
  {
    icon: Award,
    title: 'Vs les Big 4 et cabinets de conseil IA',
    description:
      "Pas de cadrage à 30 k€ avant le premier livrable. Pas de slides 80 pages. Du POC en 2-4 semaines, du livrable mesurable, à un tarif PME-ETI.",
  },
  {
    icon: Code2,
    title: 'Vs les consultants IA généralistes',
    description:
      "Eux ne connaissent pas le métier RH. Nous, on a vingt ans de direction RH derrière nous. La différence se voit au premier brief : on sait quelles données regarder, quels biais éviter, quels indicateurs vraiment utiles.",
  },
  {
    icon: Bot,
    title: 'Vs les solutions SaaS RH généralistes',
    description:
      "Workday, SAP SuccessFactors et autres ne se plient pas à vos cas d'usage spécifiques — vous payez 50 k€/an pour utiliser 20 % du produit. Nos solutions sur-mesure couvrent exactement ce dont vous avez besoin.",
  },
];

export default function IAEntreprisePage() {
  // Modale d'accès au dossier IA & Recrutement (lead capture → page gatée)
  const [dossierModalOpen, setDossierModalOpen] = useState(false);

  const navigate = useNavigate();

  return (
    <Layout
      title="IA RH sur-mesure à Marseille · POC en 2-4 semaines | recruteurs.io"
      description="Chatbots RH, IA prédictive workforce, scoring sémantique : on conçoit votre IA RH sur-mesure. Cadrage gratuit, POC en 2-4 semaines. 12 ans d'expertise terrain."
      keywords="ia rh, ia recrutement, chatbot rh, ia prédictive rh, ia générative recrutement, scoring cv ia, mapping culturel ia, transformation rh ia, conseil ia entreprise marseille"
    >
      <WebPageSchema
        page={{
          title: 'IA RH sur-mesure : chatbots, prédictif, génératif | recruteurs.io',
          description:
            "recruteurs.io conçoit et déploie des solutions IA RH sur-mesure pour les PME et ETI : chatbots, IA prédictive workforce, IA générative contenus, scoring sémantique. Expertise RH senior + ingénierie IA appliquée sous le même toit.",
          url: 'https://recruteurs.io/entreprise/ia',
          lastReviewed: '2026-05-10',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Entreprise', url: 'https://recruteurs.io/entreprise/recrutement' },
          { name: 'IA RH', url: 'https://recruteurs.io/entreprise/ia' },
        ]}
      />

      <HeroTemplateEnts
        page="ia"
        title="Construisons votre IA RH sur-mesure."
        eyebrow="Build IA RH · Chatbots · Prédictif · Génératif · Marseille · Aix · PACA"
        description="La seule agence à Marseille qui combine expertise RH senior et ingénierie IA appliquée sous le même toit. Chatbots conversationnels, IA prédictive workforce, IA générative contenus, scoring sémantique : nous concevons et déployons les solutions IA dont vos équipes RH ont besoin — pas celles que les éditeurs SaaS veulent vous vendre."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Conseil-IA-marseille.webp"
        breadcrumbs={[
          { name: 'Entreprise', href: '/entreprise/recrutement' },
          { name: 'IA RH', href: '/entreprise/ia' },
        ]}
        cta1={{ label: 'Cadrer mon projet IA RH', onClick: () => navigate('/reservation') }}
        cta2={{
          label: '📄 Accéder au dossier IA & Recrutement',
          onClick: () => setDossierModalOpen(true),
        }}
      />

      {/* SECTION 2 — Le constat */}
      <SectionBackground page="ia" section="ia-pain" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Pourquoi la PME passe à côté</span>
            <h2 className={h2Class}>
              L’IA RH est partout. Mais{' '}
              <span className={gradientText}>presque jamais chez la PME.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Trois obstacles structurels expliquent pourquoi les entreprises de
              50 à 500 collaborateurs n’en bénéficient pas — alors que ce sont
              elles qui en ont le plus besoin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {adoptionBlockers.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — Notre conviction */}
      <SectionBackground page="ia" section="ia-conviction" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre différenciateur</span>
            <h2 className={h2Class}>
              Deux métiers que personne ne combine,{' '}
              <span className={gradientText}>réunis sous le même toit.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              L’IA RH efficace exige deux compétences rares à trouver
              ensemble : la connaissance fine du métier RH ET la maîtrise des
              technologies IA modernes. C’est exactement ce que nous offrons.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {conviction.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-10 rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 max-w-4xl mx-auto">
            <p className={`${bodyClass} text-center`}>
              <strong>L’IA n’est pas un gadget chez recruteurs.io.</strong>{' '}
              Nous automatisons les process RH depuis 2017, et nous sommes
              précurseurs sur l’IA appliquée au recrutement depuis 2022 —
              bien avant la vague ChatGPT et les éditeurs SaaS qui en ont fait
              un argument marketing. Nous savons ce qui marche, et ce qui ne
              marche pas.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — 6 cas d'usage */}
      <SectionBackground page="ia" section="ia-usecases" className="bg-white">
        <Section>
          <div id="cas-usage" className="max-w-4xl mx-auto text-center mb-14 scroll-mt-24">
            <span className={eyebrowClass}>Les 6 cas d’usage IA RH</span>
            <h2 className={h2Class}>
              Six familles de solutions{' '}
              <span className={gradientText}>que nous concevons et déployons.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Chaque solution est calibrée à votre contexte, votre volumétrie,
              votre maturité tech. Pas de produit générique — du sur-mesure
              chiffré au livrable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {useCases.map((uc, index) => {
              const Icon = uc.icon;
              return (
                <motion.div
                  key={uc.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.07 }}
                  className="group rounded-[2rem] bg-white p-8 md:p-10 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className="inline-flex items-center rounded-full bg-[#fbfaf8] px-3 py-1 text-xs font-semibold text-neutral-700 uppercase tracking-widest ring-1 ring-neutral-200">
                      {uc.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-[#30475e] leading-tight mb-3">
                    {uc.title}
                  </h3>
                  <p className={`${bodyClass} mb-5`}>{uc.description}</p>

                  <ul className="space-y-2 pt-4 border-t border-neutral-100">
                    {uc.examples.map((ex) => (
                      <li
                        key={ex}
                        className="flex items-start gap-2 text-sm text-neutral-700"
                      >
                        <CheckCircle2 className="w-4 h-4 text-[#d03484] flex-shrink-0 mt-0.5" />
                        <span>{ex}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Cas client : Mapping RH */}
      <SectionBackground page="ia" section="ia-case" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>Cas concret · Mapping RH</span>
            <h2 className={h2Class}>
              Notre meilleure démonstration ?{' '}
              <span className={gradientText}>Notre propre outil.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Nous avons construit le Mapping RH pour notre usage interne et
              l’avons mis gratuitement à disposition de tous. Un test GPT-4 qui
              calcule la compatibilité candidat ↔ équipe en moins de 3 minutes.
            </p>
          </div>

          <div className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
              <div>
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3">
                  Stack technique
                </p>
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] leading-tight mb-4">
                  Edge function Supabase + GPT-4 + scoring{' '}
                  <span className={gradientText}>déterministe.</span>
                </h3>
                <p className={`${bodyClass} mb-4`}>
                  Le candidat (ou le manager) renseigne 5 sliders sur l’équipe
                  + une description du profil recherché. L’IA génère un score
                  de compatibilité 0-100, identifie les zones de friction
                  probables, et propose des recommandations actionnables pour
                  le manager.
                </p>
                <a
                  href="/ressources/mapping-rh"
                  className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  Tester l’outil →
                </a>
              </div>

              <ul className="space-y-3">
                {[
                  'Prompt engineering RH calibré sur le langage métier',
                  'Scoring déterministe (réponse cohérente sur même input)',
                  'Capture lead intégrée (Supabase, RGPD-safe)',
                  'Intégrable en iframe ou en composant React natif',
                  'Coût d’opération < 0,02 € par test',
                ].map((line) => (
                  <li
                    key={line}
                    className="flex items-start gap-3 rounded-2xl bg-[#fbfaf8] px-5 py-4 ring-1 ring-neutral-200"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#d03484] flex-shrink-0 mt-0.5" />
                    <span className="text-base text-neutral-800 leading-snug">
                      {line}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Méthode 4 étapes */}
      <SectionBackground page="ia" section="ia-method" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre méthode</span>
            <h2 className={h2Class}>
              Cadrage gratuit. POC chiffré.{' '}
              <span className={gradientText}>Mesure continue.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Pas de cabinet de conseil qui vous fait 80 slides avant de toucher
              au clavier. On construit, on mesure, on ajuste — et on s’arrête
              proprement quand c’est nécessaire.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {methodSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-6 flex items-center justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-5 h-5 text-neutral-300 group-hover:text-[#d03484] transition-colors duration-300" />
                  </div>
                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 7 — Pour qui */}
      <SectionBackground page="ia" section="ia-fit" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Pour qui</span>
            <h2 className={h2Class}>
              Quatre profils d’entreprises{' '}
              <span className={gradientText}>pour qui ça change tout.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fitProfiles.map((profile, index) => {
              const Icon = profile.icon;
              return (
                <motion.div
                  key={profile.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{profile.title}</h3>
                  <p className={bodyClass}>{profile.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 8 — Différenciateurs */}
      <SectionBackground page="ia" section="ia-diff" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre différenciateur</span>
            <h2 className={h2Class}>
              Trois alternatives,{' '}
              <span className={gradientText}>aucune à notre niveau.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {differentiators.map((d, index) => {
              const Icon = d.icon;
              return (
                <motion.div
                  key={d.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{d.title}</h3>
                  <p className={bodyClass}>{d.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 9 — Vidéo */}
      <SectionBackground page="ia" section="ia-press" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>
              <PlayCircle className="w-4 h-4 mr-2" />
              Vu à la télé
            </span>
            <h2 className={h2Class}>
              Notre vision de l’IA RH,{' '}
              <span className={gradientText}>expliquée à l’antenne.</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto rounded-[2rem] overflow-hidden ring-1 ring-neutral-200 shadow-xl">
            <div className="relative w-full" style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src="https://www.youtube.com/embed/sXY1BChmGSc"
                title="recruteurs.io passe à la télé"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 10 — CTA final */}
      <SectionBackground page="ia" section="ia-cta" className="bg-white">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Cadrons votre projet IA RH.{' '}
              <span className={gradientText}>Une heure, sans engagement.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              On regarde ensemble si l’IA est la bonne solution pour votre cas.
              Si oui, on chiffre un POC en 2-4 semaines. Si non, on vous oriente.
              Pas de gloubi-boulga IA pour faire du IA.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Cadrer mon projet IA RH (1 h)
              </button>
              <a
                href="/ressources/mapping-rh"
                className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
              >
                Tester notre Mapping RH
              </a>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 11 — Bloc SEO */}
      <SectionBackground page="ia" section="ia-seo" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto">
            <h2 className={`${h2Class} text-center`}>
              IA appliquée à la fonction RH,{' '}
              <span className={gradientText}>en quelques mots.</span>
            </h2>

            <div className="space-y-5 text-base md:text-lg text-neutral-700 leading-relaxed mt-10">
              <p>
                recruteurs.io conçoit et déploie des solutions{' '}
                <strong>IA RH sur-mesure</strong> pour les PME et ETI à
                Marseille, Aix-en-Provence et toute la PACA. Notre singularité&nbsp;:
                la combinaison rare d’une expertise RH senior (vingt ans en
                direction des ressources humaines) et d’une ingénierie IA
                appliquée (LLM, vector DBs, RAG, edge functions).
              </p>
              <p>
                Six familles de cas d’usage&nbsp;: <strong>chatbots RH</strong>{' '}
                conversationnels (assistant candidat, onboarding, collaborateur),{' '}
                <strong>IA prédictive</strong> (workforce planning, anticipation
                turnover, évolution des compétences),{' '}
                <strong>IA générative</strong> (offres d’emploi, fiches de poste,
                contenus marque employeur), <strong>IA de scoring</strong> (CV ↔
                poste, fit culturel via Mapping RH),{' '}
                <strong>IA conversationnelle</strong> (préparation candidat,
                simulations d’entretien),{' '}
                <strong>IA décisionnelle</strong> (détection RPS, recommandations
                de fidélisation).
              </p>
              <p>
                Notre méthode est éprouvée&nbsp;: cadrage gratuit (1 h), POC
                chiffré (2 à 4 semaines), déploiement opérationnel avec
                formation, mesure continue. Nous sommes la seule agence à
                Marseille à proposer cette approche — vous voulez aller voir
                ce qu’on a déjà construit&nbsp;? Testez notre{' '}
                <a
                  href="/ressources/mapping-rh"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  Mapping RH
                </a>{' '}
                en moins de 3 minutes, ou voyez nos missions de{' '}
                <a
                  href="/entreprise/recrutement"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  recrutement à l’unité
                </a>{' '}
                et de{' '}
                <a
                  href="/entreprise/campagnes"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  campagnes vivier
                </a>
                .
              </p>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* Modale d'accès au dossier IA & Recrutement (lead capture → page gatée) */}
      <IADossierModal
        open={dossierModalOpen}
        onClose={() => setDossierModalOpen(false)}
      />
    </Layout>
  );
}
